import React, { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../../../store';
import { API_GAMEKIT } from '../../../../api/gamekit';
import { ENUM_CUS_EVENT, TEST_MODE } from '../../../../enums/enums';
import { useDispatch } from 'react-redux';
import {
  setAddCollections,
  setAllCasino,
  setCategories,
  setCategoriesInfo,
  setCategoryGames,
  setCollections,
  setDeleteCollections,
  setSelectGame,
  setUpdateCollection,
  updateCategoryGame
} from '../../../../store/apps/gameProvidersSlice';
import useToast from '../../../useToast';
import useGameProviderUtils from './useGameProviderUtils';
import { dispatchCusEvent, areObjectsEqualSimple } from '../../../../utils';
import { useTranslation } from 'react-i18next';

const defaultCollectionList = [
  { id: 1, name: '2PLIT', useCasinos: [1, 14], useCategories: [1, 2] },
  { id: 2, name: 'my collection', useCasinos: [], useCategories: [3, 4, 5, 6] }
];

export default function useGameProvidersController() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { successToast, errorToast } = useToast();
  const { getCategoryName } = useGameProviderUtils();

  const tableContainerRef = useRef(null);
  const currentCollectionRef = useRef(null);

  const storeCategories = useAppSelector((state) => state.gameProviders.category.categories);
  const categoryCollectionId = useAppSelector((state) => state.gameProviders.category.collectionId);
  const collections = useAppSelector((state) => state.gameProviders.collections);
  const casinos = useAppSelector((state) => state.gameProviders.allCasino);

  const casinoById = casinos.reduce((prev, cur) => {
    prev[cur.id] = cur;
    return prev;
  }, {});

  const [selectCollectionId, setSelectCollectionId] = useState(0);
  const [selectCategoryId, setSelectCategoryId] = useState(0);

  const updateCategoryGames = () => {
    API_GAMEKIT.collection.categories.games
      .list({ categoryId: selectCategoryId })
      .then((e) => {
        const list = e.data.list;
        dispatch(setCategoryGames({ categoryId: selectCategoryId, list: list }));
      })
      .catch(console.error);
  };

  const updateCasinos = () => {
    API_GAMEKIT.collection
      .casinos()
      .then((e) => {
        const list = e.data.list;
        dispatch(setAllCasino(list));
      })
      .catch(console.error);
  };

  useEffect(() => {
    if (TEST_MODE) {
      dispatch(setCollections(defaultCollectionList));
      dispatch(setAllCasino(casinos));
      return;
    }

    API_GAMEKIT.collection
      .list()
      .then((e) => {
        const list = e.data.list;
        dispatch(setCollections(list));
      })
      .catch(console.error);

    updateCasinos();
  }, []);

  useEffect(() => {
    setSelectCategoryId(0);

    if (!selectCollectionId) return;
    if (TEST_MODE) return;

    API_GAMEKIT.collection.categories
      .list({ collectionId: selectCollectionId })
      .then((e) => {
        const list = e.data.list;
        dispatch(setCategoriesInfo({ collectionId: selectCollectionId, categories: list }));
      })
      .catch(console.error);

    getCollectionInfo(selectCollectionId);
  }, [selectCollectionId]);

  useEffect(() => {
    dispatch(setSelectGame({}));
    if (!selectCategoryId) return;

    updateCategoryGames();
  }, [selectCategoryId]);

  const addCollection = ({ name, casinoList, currencyList, mergeProvider }) => {
    const isCheck = checkCurrencyInCasino(casinoList, currencyList);
    if (!isCheck) return;
    return new Promise((rs, rj) => {
      API_GAMEKIT.collection
        .add({ name, casinoList, currencyList, mergeProvider })
        .then((e) => {
          const data = e.data.data;
          const id = data.id;
          const fullData = { name, casinoList, currencyList, mergeProvider, id };
          setSelectCollectionId(data.id);
          dispatch(setAddCollections(fullData));

          rs(id);

          setTimeout(() => {
            currentCollectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        })
        .catch((e) => {
          console.error(e);
          rj(e);
        });
    });
  };

  const checkCurrencyInCasino = (casinoListP, currencyListP, collectionId) => {
    let casinoList = casinoListP;
    let currencyList = currencyListP;

    for (const casinoId of casinoList) {
      const casino = casinoById[casinoId];
      if (!casino) continue;

      const casinoName = casino.casinoName;
      const listCollectionsId = Object.keys(casino?.collections || {});

      for (const curCollectId of listCollectionsId) {
        if (curCollectId == collectionId) continue;

        const collection = casino?.collections[curCollectId];
        const isAnyCurrency = !collection.currencyList?.length;
        const foundCurrency = collection.currencyList?.find((el) => currencyList.includes(el));
        const isCurrentCurrency = !!foundCurrency;

        // Если у казино есть коллекция c пустым списком и пустой список для добавления то нельзя добавить
        if (!currencyList.length && isAnyCurrency) {
          errorToast(
            t('The casino already has a collection supporting any currency', {
              casinoName: casinoName
            })
          );
          return false;
        }

        // Если есть поддержка всех валют или текущей валюты
        if (isCurrentCurrency) {
          errorToast(
            t('The casino already has a collection featuring the currency', {
              casinoName: casinoName,
              currency: foundCurrency
            })
          );
          return false;
        }
      }
    }

    return true;
  };

  const editCollection = (collectionId, { casinoList, currencyList, name, mergeProvider }) => {
    const isCheck = checkCurrencyInCasino(casinoList, currencyList, collectionId);
    if (!isCheck) return;

    const casinoUsed = casinos.reduce((acc, el) => {
      acc[el.id] = casinoList.includes(el.id);
      return acc;
    }, {});

    return new Promise((rs, rj) => {
      API_GAMEKIT.collection
        .update({
          casinoList: casinoUsed,
          collectionId: collectionId,
          currencyList: currencyList,
          mergeProvider,
          name: name
        })
        .then((e) => {
          const data = { name, casinoList, currencyList };
          dispatch(setUpdateCollection({ id: collectionId, mergeProvider, data }));
          updateCasinos();
          successToast('Information updated successfully');
          rs(data);
        })
        .catch((e) => {
          console.error(e);
          rj(e);
        });
    });
  };

  const deleteCollection = (collectId) => {
    API_GAMEKIT.collection
      .delete({ collectionId: collectId })
      .then(() => {
        setSelectCollectionId(0);
        dispatch(setDeleteCollections(collectId));
      })
      .catch(console.error);
  };

  const deleteCategories = (categoryIdList) => {
    API_GAMEKIT.collection.categories
      .delete({ categoryList: categoryIdList })
      .then((e) => {
        const list = e.data.list;
        dispatch(setCategories({ collectionId: selectCollectionId, list }));
        successToast('Selected categories removed');
      })
      .catch(console.error);
  };

  const addNewCategory = async (categoryInfo) => {
    await API_GAMEKIT.collection.categories
      .add({ collectionId: selectCollectionId, ...categoryInfo })
      .then((e) => {
        const list = e.data.list;

        const currentCategory = list.find((el) => {
          return areObjectsEqualSimple(el[2], categoryInfo.nameList);
        });

        dispatch(setCategories({ collectionId: selectCollectionId, list }));
        successToast('New category successfully added');
        if (currentCategory) setSelectCategoryId(currentCategory[0]);
      })
      .catch(console.error);
  };

  const updateCategory = async (categoryInfo) => {
    await API_GAMEKIT.collection.categories
      .update(categoryInfo)
      .then((e) => {
        const list = e.data.list;
        dispatch(setCategories({ collectionId: selectCollectionId, list }));
        successToast('Information updated successfully');
      })
      .catch(console.error);
  };

  const saveCategory = async (id, data) => {
    let hasAtLeastOne = false;

    const filteredNameList = {};

    for (const lang in data.nameList) {
      const value = data.nameList[lang];

      if (value.trim() !== '') {
        filteredNameList[lang] = value;
        hasAtLeastOne = true;
      }
    }

    if (!hasAtLeastOne) {
      errorToast('Please provide the title for at least one language');
      return false;
    }

    const categoryInfo = { ...data, nameList: filteredNameList };

    if (!id) {
      await addNewCategory(categoryInfo);
      return true;
    }

    await updateCategory(categoryInfo);
    setSelectCategoryId(id);
    return true;
  };

  const updateGameInfo = (gameInfo) => {
    if (!gameInfo.categoryId) {
      errorToast('Select a category for the game');
      return;
    }

    if (!gameInfo.image) {
      errorToast('Please fill in the image');
      return;
    }
    if (!gameInfo.priority) {
      errorToast('Please fill in the priority field');
      return;
    }

    const data = { ...gameInfo };
    delete data.id;
    delete data.providerName;

    API_GAMEKIT.collection.categories.games
      .save(data)
      .then((e) => {
        successToast('Information updated successfully');
        dispatch(updateCategoryGame(gameInfo));
        dispatch(setSelectGame(gameInfo));

        if (gameInfo.isUpdateCategory) {
          dispatchCusEvent(ENUM_CUS_EVENT.handlerOnSaveGameGP);
        }
      })
      .catch(console.error);
  };

  const getCollectionInfo = (id) => {
    return new Promise((rs) => {
      API_GAMEKIT.collection
        .info({ collectionId: id })
        .then((e) => {
          const data = e.data.data;
          rs(data);
          if (data) {
            dispatch(setUpdateCollection({ id, data: data }));
          }
        })
        .catch(console.error);
    });
  };

  const categories = categoryCollectionId == selectCollectionId ? storeCategories : [];

  const currentCategory = categories.find((el) => el[0] == selectCategoryId);
  const categoryName = getCategoryName(currentCategory);

  return {
    selectCollectionId,
    selectCategoryId,
    categories,
    categoryName,
    getCollectionInfo,
    setSelectCategoryId,
    deleteCategories,
    setSelectCollectionId,
    addCollection,
    editCollection,
    deleteCollection,
    updateGameInfo,
    updateCategoryGames,
    updateCasinos,
    saveCategory,
    tableContainerRef,
    currentCollectionRef
  };
}
